import { Box, IconButton, Typography } from "@mui/material"
import {
  AddOutlined,
  KeyboardArrowDownOutlined,
  KeyboardArrowUpOutlined,
  UploadFileOutlined,
} from "@mui-symbols-material/w300"
import { type FC, useState } from "react"

import { CustomerProductFileDialog } from "./CustomerProductFileDialog"
import { useCustomerProductFilesColumns } from "./useCustomerProductFilesColumns"

import { InformationMissingAlert } from "@/components/common/Alerts/InformationMissingAlert"
import { Button } from "@/components/common/Button"
import { FilesTable } from "@/components/Tables/Aeros/components/FilesTable"
import { useGetFileUploadData } from "@/components/upload/hooks/useGetFileUploadData"
import { ReferenceTableName } from "@/graphql/codegen/graphql"
import { useFilesRemove } from "@/hooks/useFilesRemove"
import { CUSTOMER_PRODUCT_FILE_FIELD_GROUPS } from "@/screens/CustomerProducts/constants"

interface CustomerProductFilesSectionProps {
  companyProductAssociationLocationId: string
}

export const CustomerProductFilesSection: FC<CustomerProductFilesSectionProps> = ({
  companyProductAssociationLocationId,
}) => {
  const [isExpanded, setIsExpanded] = useState(true)
  const [isFileFormOpen, setIsFileFormOpen] = useState(false)

  const { fileList, fileFetching, refetchFileUploadData } = useGetFileUploadData({
    referenceId: companyProductAssociationLocationId,
    fieldGroups: CUSTOMER_PRODUCT_FILE_FIELD_GROUPS,
    referenceTableName: ReferenceTableName.CustomerProductPreference,
    pause: !companyProductAssociationLocationId,
  })

  const { removeSingleFile: handleFileRemove } = useFilesRemove(refetchFileUploadData)

  const columns = useCustomerProductFilesColumns(handleFileRemove)

  const handleUploadOpen = () => setIsFileFormOpen(true)

  return (
    <Box className='rounded-md border border-solid border-gray-300 p-6'>
      <Box className='flex items-center justify-between'>
        <Typography className='text-ml font-normal text-gray-900'>Uploaded Files</Typography>
        <IconButton
          size='small'
          onClick={() => setIsExpanded((prev) => !prev)}
          data-testid='customer-product.files-section.expand-button'
        >
          {isExpanded ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
        </IconButton>
      </Box>

      {isExpanded && (
        <Box className='flex flex-col gap-y-4 pt-4'>
          {fileList.length > 0 ? (
            <Box className='flex flex-col'>
              <Button
                size='small'
                appearance='outlined'
                className='mb-4 self-end'
                startIcon={<UploadFileOutlined />}
                onClick={handleUploadOpen}
                data-testid='customer-product-files.upload-button'
              >
                Upload Files
              </Button>
              <Box className='max-h-96 overflow-auto'>
                <FilesTable data={fileList} isLoading={fileFetching} columns={columns} noDataMessage='No files found' />
              </Box>
            </Box>
          ) : (
            <InformationMissingAlert
              title='Upload Files'
              description='PDF, PNG, JPG or DOC (max. 10MB)'
              action={
                <Box className='flex h-full items-center'>
                  <Button
                    color='inherit'
                    size='small'
                    appearance='outlined'
                    className='text-primary'
                    startIcon={<AddOutlined />}
                    onClick={handleUploadOpen}
                    data-testid='customer-product-files.upload-button-empty'
                  >
                    Upload Files
                  </Button>
                </Box>
              }
            />
          )}
        </Box>
      )}

      {isFileFormOpen && !!companyProductAssociationLocationId && (
        <CustomerProductFileDialog
          onClose={() => setIsFileFormOpen(false)}
          onRefresh={refetchFileUploadData}
          companyProductAssociationLocationId={companyProductAssociationLocationId}
        />
      )}
    </Box>
  )
}
